/**
 * Report date-range translation (2026-08-25).
 *
 * The engine's range is `date_from <= x < date_to` — END-EXCLUSIVE, and
 * that stays: it is the correct convention for timestamp filtering
 * (report_engine compiles `col < :date_to`). What was user-hostile was
 * exposing it raw: two bare pickers where 8/27 → 8/27 returned zero rows
 * and staff learned the "+1 day" trick by folklore.
 *
 * So the UI is INCLUSIVE and this module is the only translation layer:
 *   picker "To"  → engine date_to   = +1 day   (pickerToEngineTo)
 *   engine date_to → picker "To"    = −1 day   (engineToPickerTo)
 * Symmetric by construction, so a saved report loads, re-runs and returns
 * byte-identical results.
 *
 * All day math runs on naive 'YYYY-MM-DD' parts via Date.UTC, and "today"
 * is the AMERICA/LOS_ANGELES calendar date — matching the engine (the
 * view's timestamps are naive Pacific wall-clock) and the day grain's
 * bucket boundaries. The previous `new Date().toISOString().slice(0,10)`
 * was the browser's UTC date, which is tomorrow for any Pacific user
 * after 5pm.
 */

export const PACIFIC_TZ = "America/Los_Angeles";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_MS = 86400000;

const parse = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  if (!m) return null;
  return { y: +m[1], m: +m[2], d: +m[3] };
};

const pad = (n) => String(n).padStart(2, "0");
const toYmd = (ts) => {
  const dt = new Date(ts);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};
const asTs = (ymd) => {
  const p = parse(ymd);
  return p ? Date.UTC(p.y, p.m - 1, p.d) : null;
};

/** Calendar date in Pacific, as 'YYYY-MM-DD'. */
export function pacificToday(now = new Date()) {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PACIFIC_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(now);
}

export function addDays(ymd, n) {
  const ts = asTs(ymd);
  return ts === null ? null : toYmd(ts + n * DAY_MS);
}

/** Inclusive picker "To" → the engine's exclusive `date_to`. */
export const pickerToEngineTo = (pickerTo) =>
  (pickerTo ? addDays(pickerTo, 1) : null);

/** The engine's exclusive `date_to` → inclusive picker "To". */
export const engineToPickerTo = (engineTo) =>
  (engineTo ? addDays(engineTo, -1) : "");

/**
 * Engine range (exclusive end) for a preset. Shape is UNCHANGED from the
 * pre-translation implementation — only the "today" anchor is now the
 * Pacific date instead of the browser's UTC date.
 */
export function rangeForPreset(preset, days = 30, now = new Date()) {
  const today = pacificToday(now);
  const p = parse(today);
  const tomorrow = addDays(today, 1);
  if (preset === "mtd") {
    return { date_from: `${p.y}-${pad(p.m)}-01`, date_to: tomorrow };
  }
  if (preset === "ytd") {
    return { date_from: `${p.y}-01-01`, date_to: tomorrow };
  }
  return { date_from: addDays(today, -days), date_to: tomorrow };
}

/** Engine range → the inclusive pair the pickers and the echo show. */
export function inclusiveRange(engineRange = {}) {
  return {
    from: engineRange.date_from || "",
    to: engineToPickerTo(engineRange.date_to),
  };
}

/** Inclusive day count, 27→27 = 1. Null when either end is missing. */
export function dayCount(fromInclusive, toInclusive) {
  const a = asTs(fromInclusive);
  const b = asTs(toInclusive);
  if (a === null || b === null) return null;
  return Math.round((b - a) / DAY_MS) + 1;
}

export function formatDay(ymd, withYear = false) {
  const p = parse(ymd);
  if (!p) return "";
  const base = `${MONTHS_SHORT[p.m - 1]} ${p.d}`;
  return withYear ? `${base}, ${p.y}` : base;
}

/** "From is after To" → a message, never a silent zero-row result. */
export function rangeValidationError(fromInclusive, toInclusive) {
  const a = asTs(fromInclusive);
  const b = asTs(toInclusive);
  if (a === null || b === null) return null;
  if (a > b) {
    return `Start date (${formatDay(fromInclusive)}) is after end date `
      + `(${formatDay(toInclusive)}) — swap them to run this report.`;
  }
  return null;
}

/**
 * The live echo under the pickers. Plain language, inclusive, always
 * derived from the same translation as the submitted range.
 */
export function rangeEcho(fromInclusive, toInclusive) {
  const err = rangeValidationError(fromInclusive, toInclusive);
  if (err) return null;
  const n = dayCount(fromInclusive, toInclusive);
  if (n === null) return null;
  const a = parse(fromInclusive);
  const b = parse(toInclusive);
  const withYear = a.y !== b.y || a.y !== parse(pacificToday()).y;
  if (n === 1) {
    return `Includes data for ${formatDay(fromInclusive, withYear)} (1 day)`;
  }
  return `Includes data for ${formatDay(fromInclusive, withYear)} – `
    + `${formatDay(toInclusive, withYear)} (${n} days)`;
}
